/**
 * PatientCasePlan.jsx — Phase 3 of TREATMENT-JOURNEY-IMPLEMENTATION-003.
 *
 * Operational Patient Case Plan view (the live, post-snapshot,
 * post-overrides surface). Used by Coordinator / Provider Admin on the
 * Patient Case page (`/clinic/cases/{caseId}/workflow`).
 *
 * Reads:
 *   GET  /api/clinic/cases/{case_id}/plan
 *   GET  /api/clinic/cases/{case_id}/plan/revisions
 * Writes:
 *   POST /api/clinic/cases/{case_id}/materialize-plan
 *   POST /api/clinic/cases/{case_id}/plan/overrides
 *
 * Surfaces (per brief PART H):
 *   - Source Treatment (template_id + version)
 *   - Offer Adjustments (list of source_offer_ids)
 *   - Campaign Benefits (list of source_campaign_ids)
 *   - Override Count
 *   - Revision History (right drawer)
 *
 * Coordinator must clearly see which rows came from Template / Offer /
 * Campaign / Override via per-row provenance pill.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { Loader2, History, Layers, Settings, RefreshCw, Plus } from 'lucide-react';

import { api } from '../../../utils/api';
import CaseMessagingAPI from '../../../services/CaseMessagingAPI';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { toast } from 'sonner';
import {
  DataGrid,
  DetailDrawer,
  DrawerField,
  FilterChips,
  Pill,
} from '../../../components/journey-ux';

const SOURCE_TONE = {
  template: 'sky',
  offer: 'amber',
  campaign: 'violet',
  override: 'rose',
};

// COA-03 — granular Type taxonomy (Brief: Requirement / Task / Document / Consent)
const _resolveType = (row) => {
  if (row.kind === 'task') return 'task';
  if (row.consent_required) return 'consent';
  if (row.document_required) return 'document';
  return 'requirement';
};
const TYPE_LABEL_KEY = { requirement: 'provider.casePlan.typeRequirement', task: 'provider.casePlan.typeTask', document: 'provider.casePlan.typeDocument', consent: 'provider.casePlan.typeConsent' };
const TYPE_TONE  = { requirement: 'sky', task: 'violet', document: 'amber', consent: 'rose' };

// COA-03 — derived priority taxonomy.
// `critical` = consent rows due before treatment day or any row already overdue
// `high`     = required doc rows + any rows due before treatment day
// `normal`   = everything else
const _resolvePriority = (row) => {
  const due = typeof row.due_offset_days === 'number' ? row.due_offset_days : null;
  if (row.consent_required && (due == null || due <= 0)) return 'critical';
  if (due != null && due < -3) return 'critical';
  if (row.document_required) return 'high';
  if (due != null && due < 0) return 'high';
  return 'normal';
};
const PRIORITY_LABEL_KEY = { critical: 'provider.casePlan.prioCritical', high: 'provider.casePlan.prioHigh', normal: 'provider.casePlan.prioNormal' };
const PRIORITY_TONE  = { critical: 'rose', high: 'amber', normal: 'slate' };

// COA-03 — Assigned User presentation. We display a short id of the
// real assignee when set; otherwise an explicit Unassigned marker
// (NOT a generic pool label).
// COA-04 — when a team roster is available we prefer the human-readable
// display name so two assignees that share a slug prefix (e.g.
// "doc-c60-…") stay distinguishable in the grid.
const _shortUser = (id) => (id ? `#${String(id).slice(0, 6)}` : null);
const _findInTeam = (team, id) => {
  if (!id || !Array.isArray(team)) return null;
  for (const g of team) {
    const hit = (g.members || []).find((m) => m.user_id === id);
    if (hit) return { ...hit, role: g.role };
  }
  return null;
};
const _displayUser = (id, team) => {
  if (!id) return null;
  const m = _findInTeam(team, id);
  if (m && m.display_name) return m.display_name;
  return _shortUser(id);
};

const STATUS_TONE = {
  active: 'sky',
  satisfied: 'emerald',
  blocked: 'rose',
  pending: 'amber',
  intake: 'amber',
  in_progress: 'amber',
  resolved: 'emerald',
};

/** Apply local overrides on top of the snapshot to produce the live view. */
function applyOverrides(snapshot, overrides) {
  const rows = [...(snapshot?.rows || [])];
  const steps = [...(snapshot?.journey_steps || [])];
  let nextRows = rows;
  let nextSteps = steps;
  (overrides || []).forEach((o) => {
    if (o.undone) return;
    const args = o.args || {};
    if (o.op === 'change_due' && args.row_id) {
      nextRows = nextRows.map((r) =>
        r.id === args.row_id
          ? { ...r, due_offset_days: args.new_due_offset_days, _override_id: o.id }
          : r,
      );
    } else if (o.op === 'change_assignee' && args.row_id) {
      nextRows = nextRows.map((r) =>
        r.id === args.row_id
          ? { ...r, default_assignee: args.new_assignee, _override_id: o.id }
          : r,
      );
    } else if (o.op === 'remove_row' && args.row_id) {
      nextRows = nextRows.filter((r) => r.id !== args.row_id);
    } else if (o.op === 'add_row' && args.row) {
      nextRows = [
        ...nextRows,
        { ...args.row, source: 'override', _override_id: o.id },
      ];
    } else if (o.op === 'add_step' && args.step) {
      nextSteps = [...nextSteps, { ...args.step, source: 'override', _override_id: o.id }];
    }
  });
  return { rows: nextRows, journey_steps: nextSteps };
}

export default function PatientCasePlan({ caseId }) {
  const { t } = useTranslation();
  const [plan, setPlan] = useState(null); // { snapshot, overrides, … } or null when not materialized
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Materialization form (only shown when plan is null).
  const [slugDraft, setSlugDraft] = useState('bbl');
  const [materializing, setMaterializing] = useState(false);
  const [includeVipOffer, setIncludeVipOffer] = useState(true);
  const [includeAutumnCampaign, setIncludeAutumnCampaign] = useState(true);

  // Revision drawer state.
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revisionsLoading, setRevisionsLoading] = useState(false);

  // Filter chip state (per source).
  const [sourceFilter, setSourceFilter] = useState('all');

  // CGA-01 skeleton state — Journey Navigator selection + Detail Drawer row.
  const [selectedStepId, setSelectedStepId] = useState('__all__');
  const [activeRow, setActiveRow] = useState(null);

  // COA-04 — assignment editor state (per-drawer).  Roster is fetched
  // once per case; the drawer reuses it for the role/user pickers.
  const [team, setTeam] = useState([]); // [{ role, members: [{user_id, display_name, email}] }]
  const [teamLoading, setTeamLoading] = useState(false);
  const [assignRole, setAssignRole] = useState('');
  const [assignUserId, setAssignUserId] = useState('');
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [assigningCaseRole, setAssigningCaseRole] = useState(null);

  // COA-07 — operational actions state (per-drawer).
  const [dueDraft, setDueDraft] = useState('');
  const [savingDue, setSavingDue] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (!caseId) return undefined;
    let cancelled = false;
    setTeamLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/clinic/cases/${caseId}/assignable-team`);
        if (!cancelled) setTeam(Array.isArray(data?.roles) ? data.roles : []);
      } catch (_e) {
        if (!cancelled) setTeam([]);
      } finally {
        if (!cancelled) setTeamLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  // Sync the editor with the currently-selected row whenever it changes.
  useEffect(() => {
    if (!activeRow) {
      setAssignRole('');
      setAssignUserId('');
      setDueDraft('');
      return;
    }
    setAssignRole(activeRow.default_role || '');
    const a = activeRow.default_assignee;
    setAssignUserId(a && a !== 'SYSTEM_POOL' ? a : '');
    setDueDraft(
      activeRow.due_offset_days === null || activeRow.due_offset_days === undefined
        ? ''
        : String(activeRow.due_offset_days)
    );
  }, [activeRow?.id, activeRow?.default_role, activeRow?.default_assignee, activeRow?.due_offset_days]);

  // CGA-01 skeleton — case summary for the planning header strip.
  const [caseSummary, setCaseSummary] = useState(null);
  const refreshCaseSummary = useCallback(async () => {
    if (!caseId) return;
    try {
      const { data } = await api.get(`/cases/${caseId}`);
      setCaseSummary(data?.case || null);
    } catch (_e) {
      setCaseSummary(null);
    }
  }, [caseId]);

  useEffect(() => { refreshCaseSummary(); }, [refreshCaseSummary]);

  // UAT COORDINATOR-ELIGIBILITY FIX — assign a canonical case role
  // through the ONE canonical authority (`PUT /api/cases/{id}/assign` →
  // `case_service.assign_case_role`), which validates the target
  // (same provider + active canonical membership + required role) before
  // writing `treatment_cases.assigned_*` and projecting the canonical
  // `message_threads.participants[]` row. Being assigned only makes the
  // person ELIGIBLE for patient selection — it grants no medical access.
  // CODEX P1 — the successful PUT already RETURNS the authoritative
  // persisted case (`{status:'ok', case:<treatment_cases doc>}`, the same
  // document and projection `GET /cases/{id}` serves). It is therefore the
  // ONLY state authority used here: no follow-up GET (whose failure used
  // to turn a persisted assignment into a false "unassigned"), and no
  // optimistic state — on failure the previous authoritative caseSummary
  // is left untouched, so the controlled <select> reverts by itself.
  const assignCaseTeamRole = useCallback(async (roleField, userId) => {
    const target = userId || 'SYSTEM_POOL';
    setAssigningCaseRole(roleField);
    try {
      const data = await CaseMessagingAPI.assignCaseRole(caseId, roleField, target);
      const updatedCase = data?.case;
      if (data?.status !== 'ok' || !updatedCase || updatedCase[roleField] !== target) {
        throw new Error('ASSIGNMENT_NOT_CONFIRMED');
      }
      setCaseSummary(updatedCase);
      toast.success(t('provider.casePlan.toastCaseTeamSaved', 'Case team updated'));
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
        || t('provider.casePlan.toastCaseTeamFailed', 'Could not update the case team'),
      );
    } finally {
      setAssigningCaseRole(null);
    }
  }, [caseId, t]);

  /* ─── Load plan ─────────────────────────────────────────────── */
  // UAT COORDINATOR-ELIGIBILITY FIX — the canonical Case team control.
  // Rendered on BOTH the materialised plan view and the "no plan yet"
  // view: a case typically has no plan before payment, which is exactly
  // when the Coordinator must be assignable.
  const caseTeamPanel = (
        <div className="w-full sm:w-auto sm:ml-auto flex flex-wrap items-end gap-3" data-testid="case-plan-case-team">
          {[
            { field: 'assigned_coordinator_id', teamRole: 'Coordinator', label: t('provider.casePlan.coordinator'), testid: 'coordinator' },
            { field: 'assigned_doctor_id', teamRole: 'Doctor', label: t('provider.casePlan.drawerRole') === 'Role' ? 'Doctor' : 'Doctor', testid: 'doctor' },
          ].map(({ field, teamRole, label, testid }) => {
            const current = caseSummary?.[field];
            const assigned = current && current !== 'SYSTEM_POOL' && current !== 'UNASSIGNED' ? current : '';
            const members = team.find((g) => g.role === teamRole)?.members || [];
            return (
              <div key={field} className="min-w-0 max-w-full">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                <select
                  value={assigned}
                  onChange={(e) => assignCaseTeamRole(field, e.target.value)}
                  disabled={teamLoading || assigningCaseRole === field}
                  data-testid={`case-team-${testid}-select`}
                  className="mt-0.5 h-7 text-[12px] border border-slate-200 rounded px-1.5 bg-white text-slate-800 disabled:bg-slate-50 w-full max-w-full sm:w-[170px]"
                >
                  <option value="">{t('provider.casePlan.unassignedShort')}</option>
                  {members.map((m) => (
                    <option key={m.user_id} value={m.user_id}>{m.display_name}</option>
                  ))}
                  {assigned && !members.some((m) => m.user_id === assigned) && (
                    <option value={assigned}>{`#${String(assigned).slice(0, 8)}`}</option>
                  )}
                </select>
                <div className="text-[10px] text-slate-500 mt-0.5 truncate" data-testid={`case-plan-header-${testid}`}>
                  {assigned
                    ? (_displayUser(assigned, team) || `#${String(assigned).slice(0, 8)}`)
                    : t('provider.casePlan.unassignedShort')}
                </div>
              </div>
            );
          })}
        </div>
  );

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/clinic/cases/${caseId}/plan`);
      setPlan(data?.patient_case_plan ?? null);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || t('provider.casePlan.toastLoadFailed');
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [caseId, t]);

  useEffect(() => {
    load();
  }, [load]);

  /* ─── Materialize ───────────────────────────────────────────── */
  const handleMaterialize = async (force = false) => {
    if (!slugDraft.trim()) return;
    setMaterializing(true);
    setError(null);
    try {
      const offer_adjustments = includeVipOffer
        ? [
            {
              offer_id: 'OFFER-VIP-001',
              label: t('provider.casePlan.vipWelcomeLabel'),
              rows: [
                {
                  id: `adj-vip-${Date.now()}`,
                  step_id: 'step-1',
                  kind: 'task',
                  name: t('provider.casePlan.vipWelcomeRowName'),
                  patient_action: '',
                  default_role: 'Coordinator',
                  document_required: false,
                  consent_required: false,
                  due_offset_days: 2,
                  status: 'active',
                },
              ],
            },
          ]
        : [];
      const campaign_benefits = includeAutumnCampaign
        ? [
            {
              campaign_id: 'CAMP-AUTUMN-2026',
              label: t('provider.casePlan.autumnPromoLabel'),
              rows: [
                {
                  id: `ben-aut-${Date.now()}`,
                  step_id: 'step-4',
                  kind: 'task',
                  name: t('provider.casePlan.autumnFollowupRowName'),
                  patient_action: '',
                  default_role: 'After Sales',
                  document_required: false,
                  consent_required: false,
                  due_offset_days: 90,
                  status: 'active',
                },
              ],
            },
          ]
        : [];
      const { data } = await api.post(
        `/clinic/cases/${caseId}/materialize-plan`,
        {
          treatment_slug: slugDraft.trim(),
          offer_adjustments,
          campaign_benefits,
          force,
        },
      );
      setPlan(data?.patient_case_plan ?? null);
      toast.success(
        force
          ? t('provider.casePlan.toastRematerialized', { v: data?.patient_case_plan?.source_template_version })
          : t('provider.casePlan.toastCreated', { v: data?.patient_case_plan?.source_template_version }),
      );
    } catch (e) {
      const detail = e?.response?.data;
      if (detail?.error === 'ALREADY_MATERIALIZED') {
        if (window.confirm(detail.message + t('provider.casePlan.confirmForceOverwriteAppendix'))) {
          await handleMaterialize(true);
          return;
        }
      } else {
        const msg = detail?.message || e.message || t('provider.casePlan.toastMaterializeFailed');
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setMaterializing(false);
    }
  };

  /* ─── Override ──────────────────────────────────────────────── */
  const fetchRevisions = useCallback(async () => {
    if (!caseId) return;
    try {
      const { data } = await api.get(`/clinic/cases/${caseId}/plan/revisions`);
      setRevisions(Array.isArray(data?.revisions) ? data.revisions : []);
    } catch (_e) {
      // silent — the inline history will just show fewer entries
    }
  }, [caseId]);

  useEffect(() => { fetchRevisions(); }, [fetchRevisions]);

  const submitOverride = async (op, args, successMsg) => {
    try {
      const { data } = await api.post(`/clinic/cases/${caseId}/plan/overrides`, { op, args });
      setPlan(data?.patient_case_plan ?? plan);
      toast.success(successMsg);
      // COA-07 — refresh the per-row history immediately.
      fetchRevisions();
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || t('provider.casePlan.toastOverrideFailed');
      toast.error(msg);
    }
  };

  /* ─── Revisions ─────────────────────────────────────────────── */
  const openRevisions = async () => {
    setRevisionsOpen(true);
    setRevisionsLoading(true);
    try {
      const { data } = await api.get(`/clinic/cases/${caseId}/plan/revisions`);
      setRevisions(Array.isArray(data?.revisions) ? data.revisions : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setRevisionsLoading(false);
    }
  };

  /* ─── Derived view ──────────────────────────────────────────── */
  // P0-C: backend now ships a composed `live_plan` with canonical
  // provenance (including `source: 'override'`). Prefer it. Fall back
  // to the local composer only if an older payload is encountered.
  const live = useMemo(
    () => (plan ? (plan.live_plan || applyOverrides(plan.snapshot, plan.overrides)) : null),
    [plan],
  );

  const stepName = useMemo(() => {
    const m = {};
    (live?.journey_steps || []).forEach((s) => {
      m[s.id] = s.name;
    });
    return m;
  }, [live]);

  const filteredRows = useMemo(() => {
    if (!live) return [];
    let rows = live.rows;
    if (sourceFilter !== 'all') rows = rows.filter((r) => r.source === sourceFilter);
    if (selectedStepId !== '__all__') rows = rows.filter((r) => r.step_id === selectedStepId);
    return rows;
  }, [live, sourceFilter, selectedStepId]);

  // CGA-01 skeleton — Journey Navigator step list with per-step counts.
  const stepNav = useMemo(() => {
    const steps = live?.journey_steps || [];
    const counts = {};
    (live?.rows || []).forEach((r) => {
      counts[r.step_id] = (counts[r.step_id] || 0) + 1;
    });
    return steps.map((s) => ({
      id: s.id,
      name: s.name,
      count: counts[s.id] || 0,
      source: s.source || 'template',
    }));
  }, [live]);

  // Reset drawer when its row disappears (e.g. after a filter change).
  // COA-04 — also sync activeRow content so role/user updates from an
  // assignment override are reflected in the drawer immediately.
  // COA-07 — extended to also detect escalation / resolution / due
  // changes so the drawer keeps in lockstep with the grid.
  useEffect(() => {
    if (!activeRow) return;
    const fresh = filteredRows.find((r) => r.id === activeRow.id);
    if (!fresh) {
      setActiveRow(null);
    } else if (
      fresh.default_role !== activeRow.default_role ||
      fresh.default_assignee !== activeRow.default_assignee ||
      fresh.due_offset_days !== activeRow.due_offset_days ||
      fresh._override_id !== activeRow._override_id ||
      fresh._escalated !== activeRow._escalated ||
      fresh._resolved !== activeRow._resolved ||
      fresh.status !== activeRow.status
    ) {
      setActiveRow(fresh);
    }
  }, [filteredRows, activeRow]);

  const filterChips = useMemo(() => {
    const counts = { all: 0, template: 0, offer: 0, campaign: 0, override: 0 };
    (live?.rows || []).forEach((r) => {
      counts.all += 1;
      if (counts[r.source] !== undefined) counts[r.source] += 1;
    });
    return [
      { id: 'all', label: t('provider.casePlan.chipAll'), count: counts.all },
      { id: 'template', label: t('provider.casePlan.chipTemplate'), count: counts.template },
      { id: 'offer', label: t('provider.casePlan.chipOffer'), count: counts.offer },
      { id: 'campaign', label: t('provider.casePlan.chipCampaign'), count: counts.campaign },
      { id: 'override', label: t('provider.casePlan.chipOverride'), count: counts.override },
    ];
  }, [live, t]);

  const activeOverrides = useMemo(
    () => (plan?.overrides || []).filter((o) => !o.undone),
    [plan],
  );

  /* ─── Columns (COA-03 — 8 brief-mandated columns) ─── */
  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: t('provider.casePlan.colItem'),
        // COA-07 — show inline ESCALATED badge so the grid visibly
        // reflects an escalation without forcing a refresh.
        render: (row) => (
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="font-medium text-slate-900 truncate">{row.name || '—'}</span>
            {row._escalated && (
              <span
                data-testid="case-plan-row-escalated-badge"
                className="shrink-0 inline-flex items-center gap-0.5 text-[9.5px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200"
                title={row._escalated_at ? t('provider.casePlan.escalatedAtTitle', { when: formatDateTimeLocale(row._escalated_at) }) : t('provider.casePlan.escalatedTitleFallback')}
              >
                {t('provider.casePlan.escalatedBadge')}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'type',
        header: t('provider.casePlan.colType'),
        width: 'w-24',
        render: (row) => {
          const typ = _resolveType(row);
          return <Pill tone={TYPE_TONE[typ]}>{t(TYPE_LABEL_KEY[typ])}</Pill>;
        },
      },
      {
        id: 'source',
        header: t('provider.casePlan.colSource'),
        width: 'w-24',
        render: (row) => (
          <Pill tone={SOURCE_TONE[row.source] || 'slate'}>
            {row._override_id ? `${row.source} ${t('provider.casePlan.sourceEditedSuffix')}` : row.source}
          </Pill>
        ),
      },
      {
        id: 'assigned_role',
        header: t('provider.casePlan.colAssignedRole'),
        width: 'w-28',
        accessor: (row) =>
          row.default_role
            ? <span className="text-slate-700">{row.default_role}</span>
            : <span className="text-slate-300">—</span>,
      },
      {
        id: 'assigned_user',
        header: t('provider.casePlan.colAssignedUser'),
        width: 'w-28',
        render: (row) => {
          if (row.default_assignee && row.default_assignee !== 'SYSTEM_POOL') {
            return <span className="text-[11px] text-slate-700">{_displayUser(row.default_assignee, team)}</span>;
          }
          return <span className="text-rose-500 text-[10.5px] uppercase tracking-wider">{t('provider.casePlan.unassigned')}</span>;
        },
      },
      {
        id: 'status',
        header: t('provider.casePlan.colStatus'),
        width: 'w-24',
        render: (row) => (
          <Pill tone={STATUS_TONE[row.status] || 'slate'}>{row.status || '—'}</Pill>
        ),
      },
      {
        id: 'priority',
        header: t('provider.casePlan.colPriority'),
        width: 'w-24',
        render: (row) => {
          const p = _resolvePriority(row);
          return <Pill tone={PRIORITY_TONE[p]}>{t(PRIORITY_LABEL_KEY[p])}</Pill>;
        },
      },
      {
        id: 'due_date',
        header: t('provider.casePlan.colDueDate'),
        width: 'w-20',
        align: 'right',
        accessor: (row) =>
          row.due_offset_days != null ? (
            <span className="font-mono text-slate-700">
              {row.due_offset_days >= 0 ? `+${row.due_offset_days}` : row.due_offset_days}d
            </span>
          ) : (
            <span className="text-slate-300">—</span>
          ),
      },
    ],
    [team, t],
  );

  /* ─── Render ───────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="py-10 text-center text-slate-500 text-sm" data-testid="case-plan-loading">
        <Loader2 className="h-5 w-5 mx-auto mb-2 animate-spin text-slate-400" />
        {t('provider.casePlan.loading')}
      </div>
    );
  }

  if (!plan) {
    // Not yet materialized — show the materialize action.
    return (
      <div data-testid="case-plan-empty" className="border border-slate-200 rounded-xl p-5 bg-white">
        <div className="flex flex-wrap justify-end mb-3">{caseTeamPanel}</div>
        <div className="flex items-center gap-2 mb-2">
          <Layers className="h-5 w-5 text-slate-500" />
          <div className="text-[13px] font-semibold text-slate-900">{t('provider.casePlan.noPlanTitle')}</div>
        </div>
        <p className="text-[12px] text-slate-600 mb-3">
          {t('provider.casePlan.noPlanBody')}
        </p>
        {error && (
          <div className="text-[12px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-2 mb-3" data-testid="case-plan-materialize-error">
            {error}
          </div>
        )}
        <div className="grid md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('provider.casePlan.sourceTreatmentSlugLabel')}
            </Label>
            <Input
              value={slugDraft}
              onChange={(e) => setSlugDraft(e.target.value)}
              data-testid="case-plan-materialize-slug"
              className="mt-1 text-[12px] h-8"
            />
          </div>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input
              type="checkbox"
              checked={includeVipOffer}
              onChange={(e) => setIncludeVipOffer(e.target.checked)}
              data-testid="case-plan-materialize-vip"
            />
            {t('provider.casePlan.includeVipOffer')}
          </label>
          <label className="flex items-center gap-2 text-[12px] text-slate-700">
            <input
              type="checkbox"
              checked={includeAutumnCampaign}
              onChange={(e) => setIncludeAutumnCampaign(e.target.checked)}
              data-testid="case-plan-materialize-campaign"
            />
            {t('provider.casePlan.includeAutumnCampaign')}
          </label>
        </div>
        <div className="mt-3">
          <Button
            onClick={() => handleMaterialize(false)}
            disabled={materializing || !slugDraft.trim()}
            data-testid="case-plan-materialize"
            className="h-8 text-[12px] bg-slate-900 hover:bg-slate-800 text-white"
          >
            {materializing ? (
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            ) : (
              <Layers className="h-3 w-3 mr-1" />
            )}
            {t('provider.casePlan.materializeBtn')}
          </Button>
        </div>
      </div>
    );
  }

  /* materialized */
  const offerIds = plan.source_offer_ids || [];
  const campIds = plan.source_campaign_ids || [];

  return (
    <div data-testid="case-plan" className="space-y-3">
      {/* Provenance strip */}
      <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="case-plan-provenance">
        <div className="grid md:grid-cols-5 gap-3 text-[11.5px]">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('provider.casePlan.sourceTreatment')}
            </div>
            <div className="text-slate-900 font-medium font-mono mt-0.5" data-testid="case-plan-source-treatment">
              {plan.source_template_id || '—'}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('provider.casePlan.templateVersion')}
            </div>
            <div className="text-slate-900 font-medium mt-0.5" data-testid="case-plan-source-version">
              v{plan.source_template_version ?? 0}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('provider.casePlan.offerAdjustments')}
            </div>
            <div className="mt-0.5 flex flex-wrap gap-1" data-testid="case-plan-offer-ids">
              {offerIds.length === 0 ? (
                <span className="text-slate-300">—</span>
              ) : (
                offerIds.map((id) => <Pill key={id} tone="amber">{id}</Pill>)
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('provider.casePlan.campaignBenefits')}
            </div>
            <div className="mt-0.5 flex flex-wrap gap-1" data-testid="case-plan-campaign-ids">
              {campIds.length === 0 ? (
                <span className="text-slate-300">—</span>
              ) : (
                campIds.map((id) => <Pill key={id} tone="violet">{id}</Pill>)
              )}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
              {t('provider.casePlan.overrides')}
            </div>
            <div className="mt-0.5" data-testid="case-plan-override-count">
              {activeOverrides.length === 0 ? (
                <span className="text-slate-700">{t('provider.casePlan.appliedZero')}</span>
              ) : (
                <Pill tone="rose">{t('provider.casePlan.appliedCount', { count: activeOverrides.length })}</Pill>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
          <Button
            size="sm"
            variant="outline"
            onClick={openRevisions}
            data-testid="case-plan-open-revisions"
            className="h-7 text-[11px]"
          >
            <History className="h-3 w-3 mr-1" />
            {t('provider.casePlan.revisionHistory')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={load}
            data-testid="case-plan-reload"
            className="h-7 text-[11px]"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            {t('provider.casePlan.reload')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              if (window.confirm(t('provider.casePlan.confirmForceRematerialize'))) {
                setSlugDraft(plan.source_template_id || 'bbl');
                handleMaterialize(true);
              }
            }}
            data-testid="case-plan-rematerialize"
            className="h-7 text-[11px]"
          >
            <Settings className="h-3 w-3 mr-1" />
            {t('provider.casePlan.rematerialize')}
          </Button>
          <span className="ml-auto text-[10px] text-slate-400 font-mono" data-testid="case-plan-applied-revision">
            {t('provider.casePlan.appliedRevisionLabel', { v: plan.applied_revision ?? 0 })}
          </span>
        </div>
      </div>

      {/* CGA-01 — Coordinator Planning Header strip */}
      <div
        className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2"
        data-testid="case-plan-header"
      >
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('provider.casePlan.patient')}</div>
          <div
            className="text-[13px] font-semibold text-slate-900 mt-0.5 font-mono"
            data-testid="case-plan-header-patient"
          >
            {caseSummary?.patient_id ? `#${String(caseSummary.patient_id).slice(0, 8)}` : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('provider.casePlan.treatment')}</div>
          <div
            className="text-[13px] font-semibold text-slate-900 mt-0.5"
            data-testid="case-plan-header-treatment"
          >
            {caseSummary?.treatment_name || plan.source_template_id || '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('provider.casePlan.caseId')}</div>
          <div
            className="text-[12px] text-slate-700 mt-0.5 font-mono"
            data-testid="case-plan-header-case-id"
          >
            #{String(caseId).slice(0, 8)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('provider.casePlan.status')}</div>
          <div className="mt-0.5" data-testid="case-plan-header-status">
            <Pill tone={STATUS_TONE[caseSummary?.status] || 'slate'}>
              {caseSummary?.status || '—'}
            </Pill>
          </div>
        </div>
        {caseTeamPanel}
      </div>

      {/* CGA-01 — top filter strip (source provenance) */}
      <FilterChips
        testIdPrefix="case-plan-filter"
        activeId={sourceFilter}
        onChange={setSourceFilter}
        chips={filterChips}
        resultLabel={filteredRows.length === 1 ? t('provider.casePlan.rowsSingular', { count: filteredRows.length }) : t('provider.casePlan.rowsPlural', { count: filteredRows.length })}
      />

      {/* CGA-01 — 3-pane skeleton: left Journey Navigator · center Planning Grid · right Detail Drawer */}
      <div
        className="flex border border-slate-200 rounded-xl bg-white overflow-hidden"
        data-testid="case-plan-3pane"
      >
        {/* Left pane — Journey Navigator */}
        <nav
          className="w-56 shrink-0 border-r border-slate-200 bg-slate-50/60 flex flex-col"
          data-testid="case-plan-journey-nav"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold border-b border-slate-200">
            {t('provider.casePlan.journey')}
          </div>
          <ul className="flex-1 overflow-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => setSelectedStepId('__all__')}
                data-testid="case-plan-journey-nav-all"
                className={`w-full flex items-center justify-between px-3 py-1.5 text-[12px] transition-colors ${
                  selectedStepId === '__all__'
                    ? 'bg-white text-slate-900 font-semibold border-l-2 border-sky-500'
                    : 'text-slate-600 hover:bg-white border-l-2 border-transparent'
                }`}
              >
                <span>{t('provider.casePlan.allSteps')}</span>
                <span className="text-[10.5px] text-slate-400 font-mono">{live?.rows?.length || 0}</span>
              </button>
            </li>
            {stepNav.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setSelectedStepId(s.id)}
                  data-testid={`case-plan-journey-nav-${s.id}`}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-[12px] text-left transition-colors ${
                    selectedStepId === s.id
                      ? 'bg-white text-slate-900 font-semibold border-l-2 border-sky-500'
                      : 'text-slate-600 hover:bg-white border-l-2 border-transparent'
                  }`}
                >
                  <span className="truncate">{s.name}</span>
                  <span className="text-[10.5px] text-slate-400 font-mono">{s.count}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Center pane — Planning Grid */}
        <div className="flex-1 min-w-0">
          <DataGrid
            testId="case-plan-grid"
            columns={columns}
            rows={filteredRows}
            rowKey={(row) => row.id}
            selectable={false}
            onRowClick={(row) => setActiveRow(row)}
            activeRowId={activeRow?.id}
            maxHeightClass="h-[640px]"
            emptyMessage={
              <span data-testid="case-plan-empty-rows">{t('provider.casePlan.noRowsFilter')}</span>
            }
          />
        </div>

        {/* COA-03 — Right pane Detail Drawer (Item · Source · Assignment · Status · Due Date · History) */}
        {activeRow && (
          <DetailDrawer
            testId="case-plan-row-drawer"
            open={!!activeRow}
            onClose={() => setActiveRow(null)}
            eyebrow={`${t(TYPE_LABEL_KEY[_resolveType(activeRow)]).toUpperCase()} · ${stepName[activeRow.step_id] || 'STEP'}`}
            title={activeRow.name || t('provider.caseWorkflow.untitledMilestone')}
            widthClass="w-80"
          >
            <div className="space-y-3" data-testid="case-plan-row-drawer-body">
              {/* — Item — */}
              <DrawerField
                label={t('provider.casePlan.drawerItem')}
                value={
                  <span className="font-medium text-slate-900">{activeRow.name || '—'}</span>
                }
                testId="case-plan-row-drawer-field-item"
              />
              <DrawerField
                label={t('provider.casePlan.drawerType')}
                value={
                  <Pill tone={TYPE_TONE[_resolveType(activeRow)]}>
                    {t(TYPE_LABEL_KEY[_resolveType(activeRow)])}
                  </Pill>
                }
                testId="case-plan-row-drawer-field-type"
              />

              {/* — Source — */}
              <div className="pt-2 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{t('provider.casePlan.drawerSource')}</div>
                <div className="flex items-center gap-2 mb-1">
                  <Pill tone={SOURCE_TONE[activeRow.source] || 'slate'}>{activeRow.source}</Pill>
                  {activeRow._override_id && (
                    <span className="text-[10px] text-rose-600 uppercase tracking-wider">{t('provider.casePlan.editedTag')}</span>
                  )}
                </div>
                {activeRow.source === 'template' && (
                  <div className="text-[10.5px] text-slate-500 font-mono">
                    {t('provider.casePlan.drawerTemplateLine', { id: activeRow.source_template_id || '—', version: activeRow.source_template_version || t('provider.casePlan.drawerTemplateUnknownVersion') })}
                  </div>
                )}
                {activeRow.source === 'offer' && (
                  <div className="text-[10.5px] text-slate-500 font-mono">
                    {t('provider.casePlan.drawerOfferLine', { id: activeRow.source_offer_id ? `#${String(activeRow.source_offer_id).slice(0, 8)}` : '—' })}
                  </div>
                )}
                {activeRow.source === 'campaign' && (
                  <div className="text-[10.5px] text-slate-500 font-mono">
                    {t('provider.casePlan.drawerCampaignLine', { id: activeRow.source_campaign_id ? `#${String(activeRow.source_campaign_id).slice(0, 8)}` : '—' })}
                  </div>
                )}
                {activeRow.source === 'override' && (
                  <div className="text-[10.5px] text-slate-500 font-mono">
                    {t('provider.casePlan.drawerOverrideLine', { id: activeRow.source_override_id ? `#${String(activeRow.source_override_id).slice(0, 8)}` : '—' })}
                  </div>
                )}
              </div>

              {/* — Assignment (COA-04 — editable Role + User) — */}
              <div className="pt-2 border-t border-slate-100" data-testid="case-plan-row-drawer-assignment">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    {t('provider.casePlan.drawerAssignment')}
                  </div>
                  {savingAssignment && (
                    <Loader2 className="h-3 w-3 animate-spin text-slate-400" />
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-slate-700">{t('provider.casePlan.drawerCurrentRole')}</span>
                  <span className="text-[12px] text-slate-900 font-medium" data-testid="case-plan-row-drawer-field-assigned-role">
                    {activeRow.default_role || '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[12px] text-slate-700">{t('provider.casePlan.drawerCurrentUser')}</span>
                  <span className="text-[12px]" data-testid="case-plan-row-drawer-field-assigned-user">
                    {activeRow.default_assignee && activeRow.default_assignee !== 'SYSTEM_POOL' ? (
                      <span className="text-slate-900">{_displayUser(activeRow.default_assignee, team)}</span>
                    ) : (
                      <span className="text-rose-500 uppercase text-[10.5px] tracking-wider">{t('provider.casePlan.unassigned')}</span>
                    )}
                  </span>
                </div>
                <div className="mt-2 space-y-1.5">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('provider.casePlan.drawerRole')}</Label>
                    <select
                      value={assignRole}
                      onChange={(e) => { setAssignRole(e.target.value); setAssignUserId(''); }}
                      data-testid="case-plan-row-drawer-role-select"
                      className="mt-0.5 w-full h-7 text-[12px] border border-slate-200 rounded px-1.5 bg-white text-slate-800 disabled:bg-slate-50"
                      disabled={savingAssignment || teamLoading}
                    >
                      <option value="">{t('provider.casePlan.drawerRolePlaceholder')}</option>
                      {team.map((g) => (
                        <option key={g.role} value={g.role}>{g.role}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t('provider.casePlan.drawerUser')}</Label>
                    <select
                      value={assignUserId}
                      onChange={(e) => setAssignUserId(e.target.value)}
                      data-testid="case-plan-row-drawer-user-select"
                      className="mt-0.5 w-full h-7 text-[12px] border border-slate-200 rounded px-1.5 bg-white text-slate-800 disabled:bg-slate-50"
                      disabled={savingAssignment || teamLoading || !assignRole}
                    >
                      <option value="">{t('provider.casePlan.drawerUserPlaceholder')}</option>
                      {(team.find((g) => g.role === assignRole)?.members || []).map((m) => (
                        <option key={m.user_id} value={m.user_id}>
                          {m.display_name}{m.email ? ` · ${m.email}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (!assignRole) { toast.error(t('provider.casePlan.toastPickRole')); return; }
                      setSavingAssignment(true);
                      try {
                        const userLabel = assignUserId
                          ? (_findInTeam(team, assignUserId)?.display_name || `#${assignUserId.slice(0, 6)}`)
                          : t('provider.casePlan.unassignedShort');
                        await submitOverride(
                          'change_assignment',
                          { row_id: activeRow.id, new_role: assignRole, new_assignee: assignUserId || null },
                          t('provider.casePlan.toastAssignedLine', { name: activeRow.name, role: assignRole, user: userLabel }),
                        );
                      } finally {
                        setSavingAssignment(false);
                      }
                    }}
                    disabled={savingAssignment || !assignRole}
                    data-testid="case-plan-row-drawer-save-assignment"
                    className="h-7 text-[11px] mt-1 w-full bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    {savingAssignment ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    {t('provider.casePlan.drawerSaveAssignment')}
                  </Button>
                </div>
              </div>

              {/* — Status + Priority — */}
              <div className="pt-2 border-t border-slate-100 space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-slate-700">{t('provider.casePlan.drawerStatus')}</span>
                  <Pill tone={STATUS_TONE[activeRow.status] || 'slate'}>{activeRow.status || '—'}</Pill>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-slate-700">{t('provider.casePlan.drawerPriority')}</span>
                  <Pill tone={PRIORITY_TONE[_resolvePriority(activeRow)]}>
                    {t(PRIORITY_LABEL_KEY[_resolvePriority(activeRow)])}
                  </Pill>
                </div>
              </div>

              {/* — Due Date (COA-07 — proper inline editor, not prompt) — */}
              <div className="pt-2 border-t border-slate-100" data-testid="case-plan-row-drawer-due-block">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-slate-700">{t('provider.casePlan.drawerDueDate')}</span>
                  <span className="text-[12px] text-slate-900 font-mono" data-testid="case-plan-row-drawer-field-due">
                    {activeRow.due_offset_days != null
                      ? `${activeRow.due_offset_days >= 0 ? '+' : ''}${activeRow.due_offset_days}d`
                      : '—'}
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-1.5">
                  <input
                    type="number"
                    step="1"
                    value={dueDraft}
                    onChange={(e) => setDueDraft(e.target.value)}
                    placeholder={t('provider.casePlan.drawerDuePlaceholder')}
                    data-testid="case-plan-row-drawer-due-input"
                    className="h-7 flex-1 text-[12px] border border-slate-200 rounded px-2 bg-white text-slate-800 font-mono"
                    disabled={savingDue}
                  />
                  <Button
                    size="sm"
                    onClick={async () => {
                      if (dueDraft === '' || dueDraft === null) { toast.error(t('provider.casePlan.toastDueRequired')); return; }
                      const n = Number(dueDraft);
                      if (Number.isNaN(n)) { toast.error(t('provider.casePlan.toastDueMustBeNumber')); return; }
                      setSavingDue(true);
                      try {
                        await submitOverride(
                          'change_due',
                          { row_id: activeRow.id, new_due_offset_days: n },
                          t('provider.casePlan.toastDueSetLine', { sign: n >= 0 ? '+' : '', value: n, name: activeRow.name }),
                        );
                      } finally { setSavingDue(false); }
                    }}
                    disabled={savingDue}
                    data-testid="case-plan-row-drawer-save-due"
                    className="h-7 px-3 text-[11px] bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    {savingDue ? <Loader2 className="h-3 w-3 animate-spin" /> : t('provider.casePlan.drawerSave')}
                  </Button>
                </div>
              </div>

              {/* — Operational Actions (COA-07 — escalate + resolve) — */}
              <div
                className="pt-2 border-t border-slate-100"
                data-testid="case-plan-row-drawer-actions-block"
              >
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                  {t('provider.casePlan.drawerOperationalActions')}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button
                    size="sm"
                    variant={activeRow._escalated ? 'default' : 'outline'}
                    onClick={async () => {
                      setEscalating(true);
                      try {
                        const next = !activeRow._escalated;
                        await submitOverride(
                          'escalate_row',
                          { row_id: activeRow.id, escalated: next },
                          next
                            ? t('provider.casePlan.toastEscalated', { name: activeRow.name })
                            : t('provider.casePlan.toastUnescalated', { name: activeRow.name }),
                        );
                      } finally { setEscalating(false); }
                    }}
                    disabled={escalating}
                    data-testid="case-plan-row-drawer-escalate"
                    className={
                      activeRow._escalated
                        ? 'h-7 text-[11px] bg-rose-600 hover:bg-rose-700 text-white'
                        : 'h-7 text-[11px] border-rose-300 text-rose-700 hover:bg-rose-50'
                    }
                  >
                    {escalating ? <Loader2 className="h-3 w-3 animate-spin" /> : activeRow._escalated ? t('provider.casePlan.drawerUnescalate') : t('provider.casePlan.drawerEscalate')}
                  </Button>
                  <Button
                    size="sm"
                    variant={activeRow._resolved || activeRow.status === 'satisfied' ? 'default' : 'outline'}
                    onClick={async () => {
                      const currentlyResolved = !!(activeRow._resolved || activeRow.status === 'satisfied');
                      setResolving(true);
                      try {
                        const next = !currentlyResolved;
                        await submitOverride(
                          'resolve_row',
                          { row_id: activeRow.id, resolved: next },
                          next
                            ? t('provider.casePlan.toastResolved', { name: activeRow.name })
                            : t('provider.casePlan.toastReopened', { name: activeRow.name }),
                        );
                      } finally { setResolving(false); }
                    }}
                    disabled={resolving}
                    data-testid="case-plan-row-drawer-resolve"
                    className={
                      activeRow._resolved || activeRow.status === 'satisfied'
                        ? 'h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                    }
                  >
                    {resolving ? <Loader2 className="h-3 w-3 animate-spin" /> : (activeRow._resolved || activeRow.status === 'satisfied') ? t('provider.casePlan.drawerReopen') : t('provider.casePlan.drawerMarkResolved')}
                  </Button>
                </div>
              </div>

              {/* — History (COA-07 — per-row timeline from plan revisions) — */}
              <div className="pt-2 border-t border-slate-100">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">
                  {t('provider.casePlan.drawerHistory')}
                </div>
                <ul
                  className="space-y-1 text-[11px] text-slate-600"
                  data-testid="case-plan-row-drawer-history"
                >
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 font-mono">·</span>
                    <span>
                      {t('provider.casePlan.drawerMaterializedFrom')} <strong className="font-semibold capitalize">{activeRow.source}</strong>
                      {activeRow.source === 'template' && activeRow.source_template_version != null
                        ? t('provider.casePlan.drawerMaterializedVersion', { v: activeRow.source_template_version })
                        : ''}
                    </span>
                  </li>
                  {(revisions || [])
                    .filter((rev) => {
                      const args = (rev?.after && rev.after.args) || {};
                      return args.row_id === activeRow.id;
                    })
                    .map((rev) => {
                      const op = (rev.after && rev.after.op) || rev.action || '';
                      const args = (rev.after && rev.after.args) || {};
                      const label = (() => {
                        if (op === 'change_assignment') {
                          const u = args.new_assignee ? (_findInTeam(team, args.new_assignee)?.display_name || `#${String(args.new_assignee).slice(0,6)}`) : t('provider.casePlan.unassignedShort');
                          return t('provider.casePlan.drawerHistoryAssignedTo', { role: args.new_role || t('provider.casePlan.drawerHistoryRoleUnchanged'), user: u });
                        }
                        if (op === 'change_due') return t('provider.casePlan.drawerHistoryDueLine', { sign: args.new_due_offset_days >= 0 ? '+' : '', value: args.new_due_offset_days });
                        if (op === 'escalate_row') return args.escalated === false ? t('provider.casePlan.drawerHistoryUnescalated') : t('provider.casePlan.drawerHistoryEscalated');
                        if (op === 'resolve_row') return args.resolved === false ? t('provider.casePlan.drawerHistoryReopened') : t('provider.casePlan.drawerHistoryResolved');
                        if (op === 'change_assignee') return t('provider.casePlan.drawerHistoryAssigneeShort', { id: String(args.new_assignee || '').slice(0,6) });
                        if (op === 'remove_row') return t('provider.casePlan.drawerHistoryRowRemoved');
                        return op || t('provider.casePlan.drawerHistoryEditFallback');
                      })();
                      const tone = op === 'escalate_row' ? 'text-rose-500' : op === 'resolve_row' ? 'text-emerald-600' : 'text-slate-500';
                      const when = rev.created_at ? formatDateTimeLocale(rev.created_at) : '';
                      return (
                        <li key={rev.id || rev._id || `${op}-${when}`} className="flex items-start gap-2" data-testid="case-plan-row-drawer-history-entry">
                          <span className={`font-mono ${tone}`}>·</span>
                          <span className="flex-1">
                            <span className="text-slate-800">{label}</span>
                            <span className="block text-slate-400 text-[10px]">{when}{rev.actor ? ` · #${String(rev.actor).slice(0,6)}` : ''}</span>
                          </span>
                        </li>
                      );
                    })}
                  {(revisions || []).filter((rev) => ((rev?.after && rev.after.args) || {}).row_id === activeRow.id).length === 0 && !activeRow._override_id && (
                    <li className="text-slate-400 text-[10.5px]">
                      {t('provider.casePlan.drawerNoEventsYet')}
                    </li>
                  )}
                </ul>
                <button
                  type="button"
                  onClick={openRevisions}
                  data-testid="case-plan-row-drawer-open-revisions"
                  className="mt-2 text-[11px] text-sky-700 hover:underline"
                >
                  {t('provider.casePlan.drawerOpenFullHistory')}
                </button>
              </div>
            </div>
          </DetailDrawer>
        )}
      </div>

      {/* Revision drawer */}
      <DetailDrawer
        testId="case-plan-revision-drawer"
        open={revisionsOpen}
        onClose={() => setRevisionsOpen(false)}
        eyebrow={t('provider.casePlan.revisionDrawerEyebrow')}
        title={t('provider.casePlan.revisionDrawerTitle')}
        widthClass="w-96"
      >
        {revisionsLoading ? (
          <div className="text-center text-slate-500" data-testid="case-plan-revisions-loading">
            <Loader2 className="h-4 w-4 mx-auto mb-2 animate-spin" />
            {t('provider.casePlan.revisionsLoading')}
          </div>
        ) : revisions.length === 0 ? (
          <div className="text-slate-500 text-[12px]" data-testid="case-plan-revisions-empty">
            {t('provider.casePlan.revisionsEmpty')}
          </div>
        ) : (
          <ul className="space-y-2" data-testid="case-plan-revisions-list">
            {revisions.map((r) => (
              <li
                key={r.revision_id}
                className="border border-slate-100 rounded p-2 text-[11px]"
                data-testid={`case-plan-revision-${r.revision_id}`}
              >
                <div className="flex items-center gap-2">
                  <Pill tone={r.action.startsWith('override') ? 'rose' : 'sky'}>
                    {r.action}
                  </Pill>
                  <span className="text-slate-400 ml-auto">
                    {formatDateTimeLocale(r.created_at)}
                  </span>
                </div>
                <div className="text-slate-600 mt-1.5">
                  <span className="text-slate-400 font-mono">{(r.actor || '').slice(0, 8)}…</span>
                </div>
                {r.after && (
                  <pre className="mt-1 text-[10px] bg-slate-50 border border-slate-100 rounded p-1.5 overflow-auto text-slate-600">
                    {JSON.stringify(r.after, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        )}
      </DetailDrawer>
    </div>
  );
}
